import React, { useEffect, useState } from "react";

/** @typedef {import('../models/RpaServiceModel.js').RpaServiceItem} RpaServiceItem */
/** @typedef {import('../models/RpaResourceModel.js').RpaResourceItem} RpaResourceItem */
/** @typedef {import('../models/RpaServiceRuleModel.js').RpaServiceRuleItem} RpaServiceRuleItem */

/**
 * @typedef {object} RpaPeopleReservationParams
 * @property {number} nbPeoplesMin - Nombre minimum de personnes que l'utilisateur peut renseigner pour cette resource si la propriété choosePeopleReservations est à true ou si une option avec choose_nb_peoples est sélectionnée
 * @property {number} nbPeoplesMax - Nombre maximum de personnes que l'utilisateur peut renseigner pour cette resource si la propriété choosePeopleReservations est à true ou si une option avec choose_nb_peoples est sélectionnée
 * @property {number} rpaResourceId - Identifiant de la resource
 * @property {0|1} priceType - Type de la tarification, heures pleines 0 et heures creuses 1
 * @property {Object.<string, number>} totalPeoplesObj - Objet de la répartition du nombre de personnes par règle de service
 * @property {(totalPeoplesObj: Object.<string, number>) => void} refreshTotalPeoples - Fonction de rafraîchissement du total des personnes
 */

/**
 * @param {RpaPeopleReservationParams} props
 */
export default function RpaPeopleReservation({ nbPeoplesMin, nbPeoplesMax, rpaResourceId, priceType, totalPeoplesObj, refreshTotalPeoples }) {
  const [rpaService, setRpaService] = useState(/** @type {?RpaServiceItem} */ (null));
  const minPeoples = Number.parseInt(String(nbPeoplesMin || 0), 10) || 0;
  const maxPeoples = Number.parseInt(String(nbPeoplesMax || 0), 10) || 0;

  /**
   * @param {*} value
   * @returns {number}
   */
  function _toInt(value) {
    const parsedValue = Number.parseInt(String(value), 10);

    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }

  /**
   * @param {RpaServiceRuleItem} [currentRpaServiceRule]
   * @returns {number}
   */
  function _getRemainingForSelect(currentRpaServiceRule) {
    let totalFromOtherSelects = 0;

    if (!currentRpaServiceRule) {
      totalFromOtherSelects = (rpaService?.rpa_service_rules || []).reduce((acc, rpaServiceRule) => {
        return acc + _toInt(totalPeoplesObj[`nb_rpaServiceRule_${rpaServiceRule.id}`]);
      }, 0);
    } else {
      totalFromOtherSelects =
        _toInt(totalPeoplesObj.nb_adult) +
        (rpaService?.rpa_service_rules || []).reduce((acc, rpaServiceRule) => {
          if (rpaServiceRule.id === currentRpaServiceRule.id) {
            return acc;
          }

          return acc + _toInt(totalPeoplesObj[`nb_rpaServiceRule_${rpaServiceRule.id}`]);
        }, 0);
    }

    return Math.max(0, maxPeoples - totalFromOtherSelects);
  }

  /**
   * @param {RpaServiceRuleItem} [currentRpaServiceRule]
   * @returns {number}
   */
  function _getMinForSelect(currentRpaServiceRule) {
    const totalFromOtherFields = maxPeoples - _getRemainingForSelect(currentRpaServiceRule);
    return Math.max(0, minPeoples - totalFromOtherFields);
  }

  useEffect(() => {
    if (rpaResourceId && priceType !== undefined) {
      _getRpaService();
    }
  }, [rpaResourceId, priceType]);

  /**
   * Permet de récupérer la liste des stationnements associés à une ressource et de mettre à jour le state
   * @private
   */
  async function _getRpaService() {
    const apiUrl = `/api/rpa-reservation/unicservice/_search?rpaResourceId=${rpaResourceId}&priceType=${priceType}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      }),
      data = await response.json();

    setRpaService(data);
  }

  /**
   *
   * @param {import('react').ChangeEvent} e - événement associé au changement de valeur
   * @param {RpaServiceRuleItem} [rpaServiceRule] - Règle de service associé au changement de valeur
   */
  function _onChangeOnInput(e, rpaServiceRule) {
    var target = /** @type {HTMLInputElement} */ (e.target),
      value = _toInt(target.value),
      maxSelectableForCurrentField = _getRemainingForSelect(rpaServiceRule),
      totalFromOtherFields = maxPeoples - maxSelectableForCurrentField,
      minForThisField = Math.max(0, minPeoples - totalFromOtherFields),
      boundedValue = Math.min(Math.max(value, minForThisField), maxSelectableForCurrentField),
      newTotalPeoplesObj = { ...totalPeoplesObj };

    if (rpaServiceRule) {
      newTotalPeoplesObj[`nb_rpaServiceRule_${rpaServiceRule.id}`] = boundedValue;
    } else {
      newTotalPeoplesObj.nb_adult = boundedValue;
    }

    refreshTotalPeoples(newTotalPeoplesObj);
  }

  return (
    <div className="grid grid--has-4-child grid--has-2-child-mobile no-mobile">
      <div className="input-group input-group--no-margin">
        {rpaService?.rpa_service_rules.length ? (
          <label htmlFor="adult" className="input-group__label">
            Adulte
          </label>
        ) : (
          ""
        )}
        <select className="input-group__input" id="adult" onChange={e => _onChangeOnInput(e)} value={_toInt(totalPeoplesObj.nb_adult) || ""}>
          {_getMinForSelect() === 0 ? <option value="">Sélectionner une valeur</option> : null}
          {Array.from({ length: _getRemainingForSelect() }, (_, i) =>
            i + 1 >= _getMinForSelect() ? (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ) : null
          )}
        </select>
      </div>
      {rpaService ? (
        <>
          {rpaService.rpa_service_rules.map((rpaServiceRule, index) => (
            <div className="input-group input-group--no-margin" key={index}>
              <label htmlFor={`rpaServiceRule_${rpaServiceRule.id}`} className="input-group__label">
                De {rpaServiceRule.min_age} ans à {rpaServiceRule.max_age} ans
              </label>
              <select className="input-group__input" name={`nbRpaServiceRule_${rpaServiceRule.id}`} id={`rpaServiceRule_${rpaServiceRule.id}`} onChange={e => _onChangeOnInput(e, rpaServiceRule)} aria-label={`Nombre de ${rpaServiceRule.min_age} ans à ${rpaServiceRule.max_age} ans`} value={_toInt(totalPeoplesObj[`nb_rpaServiceRule_${rpaServiceRule.id}`]) || ""}>
                {_getMinForSelect(rpaServiceRule) === 0 ? <option value="">Sélectionner une valeur</option> : null}
                {Array.from({ length: _getRemainingForSelect(rpaServiceRule) }, (_, i) =>
                  i + 1 >= _getMinForSelect(rpaServiceRule) ? (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ) : null
                )}
              </select>
              <p className="input-group__help input-group__help--info">Une carte d'identité vous sera demandée.</p>
            </div>
          ))}
        </>
      ) : (
        ""
      )}
    </div>
  );
}
